import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { MapPin, X, AlertTriangle } from 'lucide-react';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';
import { backgroundGeolocationService } from '../services/background-geolocation.service';

interface Location {
  latitude: number;
  longitude: number;
  accuracy?: number | null; // GPS horizontal accuracy in meters (from position.coords.accuracy)
  timestamp: number; // When this location was captured
}

interface LocationContextType {
  location: Location | null;
  permissionStatus: 'unknown' | 'granted' | 'denied' | 'prompt';
  isLoading: boolean;
  error: string | null;
  requestLocation: () => Promise<Location | null>;
  ensureFreshLocation: () => Promise<Location | null>; // Check freshness and refresh if stale
  forceRefreshLocation: (bypassCache?: boolean) => Promise<Location | null>; // Always get fresh GPS, bypassCache=true skips 30s optimization
  isLocationFresh: () => boolean; // Check if current location is fresh
  clearLocation: () => void;
  getLocationAge: () => number | null; // Returns age in milliseconds
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

const LOCATION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
const LOCATION_STORAGE_KEY = 'bradanna_cached_location';

// A fix worse than this is a cell-tower / early-Wi-Fi guess, not GPS. The first
// answer after a cold start is often ±1-2 km; it used to be cached as "fresh" for
// 5 minutes, so every later request handed the same coarse point back and the
// clock page said "not at any job site" at the person's own house (2026-10-30).
// Such a fix is still shown (better than nothing) but is never treated as fresh,
// and the browser path keeps listening for GPS to catch up (see refineBrowserFix).
const COARSE_ACCURACY_M = 150;
const REFINE_WINDOW_MS = 8000;

const isUsableFix = (loc: Location | null | undefined): boolean =>
  !!loc && (loc.accuracy == null || loc.accuracy <= COARSE_ACCURACY_M);

const toLocation = (position: GeolocationPosition): Location => ({
  latitude: position.coords.latitude,
  longitude: position.coords.longitude,
  accuracy: position.coords.accuracy ?? null,
  timestamp: Date.now()
});

/**
 * Browser only. Given a coarse first fix, watch for up to REFINE_WINDOW_MS and
 * resolve with the best sample seen, early as soon as one is within
 * COARSE_ACCURACY_M. Never rejects: on any error it returns the best so far.
 */
const refineBrowserFix = (initial: Location, windowMs: number = REFINE_WINDOW_MS): Promise<Location> =>
  new Promise((resolve) => {
    let best = initial;
    let done = false;
    let watchId: number | null = null;
    const finish = () => {
      if (done) return;
      done = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      clearTimeout(timer);
      console.log(`[LocationContext] Refine finished: ±${best.accuracy == null ? '?' : Math.round(best.accuracy)} m (started at ±${initial.accuracy == null ? '?' : Math.round(initial.accuracy)} m)`);
      resolve(best);
    };
    const timer = setTimeout(finish, windowMs);
    try {
      watchId = navigator.geolocation.watchPosition(
        (p) => {
          const acc = p.coords.accuracy ?? null;
          if (acc != null && (best.accuracy == null || acc < best.accuracy)) best = toLocation(p);
          if (isUsableFix(best)) finish();
        },
        () => finish(),
        { enableHighAccuracy: true, maximumAge: 0, timeout: windowMs }
      );
    } catch {
      finish();
    }
  });

/**
 * Check if running as native iOS/Android app
 * Used to switch between Capacitor and browser geolocation APIs
 */
const isNativeApp = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if device is iOS (for PWA-specific handling)
 */
const isIOSDevice = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

/**
 * Track if BackgroundGeolocation has been initialized
 */
let bgGeoReady = false;
let geofenceListenerAdded = false;

/**
 * Store geofence event to localStorage for debug history
 */
const storeGeofenceEvent = (identifier: string, action: 'ENTER' | 'EXIT' | 'DWELL') => {
  try {
    const historyJson = localStorage.getItem('debug_geofence_history');
    const history = historyJson ? JSON.parse(historyJson) : [];

    // Add new event with timestamp
    const event = {
      identifier,
      action,
      timestamp: new Date().toLocaleTimeString()
    };
    history.push(event);

    // Keep only last 50 events
    const trimmed = history.slice(-50);
    localStorage.setItem('debug_geofence_history', JSON.stringify(trimmed));

    console.log(`[Geofence] ${action}: ${identifier}`);
  } catch (err) {
    console.error('[Geofence] Error storing event:', err);
  }
};

/**
 * Set up global geofence listener for debug history
 */
const setupGeofenceListener = () => {
  if (geofenceListenerAdded || !isNativeApp()) return;

  try {
    BackgroundGeolocation.onGeofence((event) => {
      console.log('[Geofence] Event received:', event.identifier, event.action);
      storeGeofenceEvent(event.identifier, event.action as 'ENTER' | 'EXIT' | 'DWELL');
    });
    geofenceListenerAdded = true;
    console.log('[LocationContext] Geofence listener added for debug history');
  } catch (err) {
    console.error('[LocationContext] Failed to add geofence listener:', err);
  }
};

/**
 * Ensure BackgroundGeolocation plugin is ready before using it
 * Must be called before any other BackgroundGeolocation method
 *
 * Delegates to the background geolocation service which owns the single ready() config.
 * IMPORTANT: Only one ready() call should exist — the service's initialize() method.
 * Calling ready() with different config in multiple places causes unpredictable behavior
 * because the plugin only applies config from the FIRST ready() call ever.
 */
const ensureBgGeoReady = async (): Promise<void> => {
  if (bgGeoReady) return;

  try {
    // Use the service's initialize() — single source of truth for plugin config
    await backgroundGeolocationService.initialize();
    bgGeoReady = true;
    console.log('[LocationContext] BackgroundGeolocation ready via service');

    // Set up geofence listener for debug history
    setupGeofenceListener();
  } catch (error) {
    console.error('[LocationContext] Failed to initialize BackgroundGeolocation:', error);
    throw error;
  }
};

/**
 * Request location using Transistorsoft BackgroundGeolocation
 * Uses the same plugin as background tracking to avoid duplicate permission prompts
 * getCurrentPosition() will trigger the permission prompt if needed
 */
const requestNativeLocation = async (timeout: number = 5000): Promise<Location> => {
  // Ensure plugin is initialized before any calls
  await ensureBgGeoReady();

  // Get current position using BackgroundGeolocation
  // This uses the same plugin as background tracking, avoiding duplicate permission prompts
  // Use MEDIUM accuracy for faster response - geofences are 50-100m so we don't need GPS precision
  const position = await BackgroundGeolocation.getCurrentPosition({
    samples: 1,
    timeout: Math.floor(timeout / 1000), // Convert ms to seconds
    desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_MEDIUM, // Faster than HIGH, still accurate enough for geofencing
    persist: false // Don't persist this location to the plugin's database
  });

  // The plugin can hand back a cached last-known sample; position.timestamp is
  // when that sample was actually taken. Stamping Date.now() here let a day-old
  // fix pass every 5-min freshness check and clock a worker into the wrong job.
  const sampleTime = position.timestamp ? new Date(position.timestamp).getTime() : NaN;
  if (Number.isFinite(sampleTime) && Date.now() - sampleTime > LOCATION_CACHE_DURATION) {
    throw new Error(`Location fix is ${Math.round((Date.now() - sampleTime) / 60000)} min old — no fresh GPS available`);
  }

  return {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy ?? null,
    timestamp: Number.isFinite(sampleTime) ? sampleTime : Date.now()
  };
};

export const LocationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // NOTE: admins used to have every location error swallowed here ("location is
  // optional for them"). That made a failed fix look like "location not found"
  // with no reason on the clock page and an empty "At Your Location" list in
  // Machine Hub (2026-10-30). Admins are still never BLOCKED from navigating
  // (AdminDashboard.navigateWithLocationRefresh), but the error is now reported
  // for everyone so it can be seen and fixed.

  // Initialize location from localStorage on mount
  const [location, setLocation] = useState<Location | null>(() => {
    try {
      const cached = localStorage.getItem(LOCATION_STORAGE_KEY);
      if (cached) {
        const parsed: Location = JSON.parse(cached);
        // Check if cached location is still within 5-minute window
        const age = Date.now() - parsed.timestamp;
        if (age < LOCATION_CACHE_DURATION && isUsableFix(parsed)) {
          console.log('[LocationContext] Restored location from localStorage (age: ' + Math.round(age / 1000 / 60) + ' min)');
          return parsed;
        } else {
          console.log('[LocationContext] Cached location expired, clearing');
          localStorage.removeItem(LOCATION_STORAGE_KEY);
        }
      }
    } catch (error) {
      console.error('[LocationContext] Error reading cached location:', error);
      localStorage.removeItem(LOCATION_STORAGE_KEY);
    }
    return null;
  });

  const [permissionStatus, setPermissionStatus] = useState<'unknown' | 'granted' | 'denied' | 'prompt'>('unknown');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showIOSPermissionModal, setShowIOSPermissionModal] = useState(false);

  // Track in-flight requests to prevent duplicate permission prompts
  const requestInProgressRef = useRef(false);
  const pendingPromiseRef = useRef<Promise<Location | null> | null>(null);

  /**
   * Get the age of the current cached location in milliseconds
   * Returns null if no location is cached
   */
  const getLocationAge = useCallback((): number | null => {
    if (!location) return null;
    return Date.now() - location.timestamp;
  }, [location]);

  /**
   * Check if cached location is still fresh (< 5 minutes old) AND not a coarse
   * fix — a ±2 km point is never "fresh enough" to skip asking the phone again.
   */
  const isCachedLocationFresh = useCallback((): boolean => {
    const age = getLocationAge();
    return age !== null && age < LOCATION_CACHE_DURATION && isUsableFix(location);
  }, [getLocationAge, location]);

  /**
   * Public method to check if location is fresh
   * Can be called before navigating to check if refresh is needed
   */
  const isLocationFresh = useCallback((): boolean => {
    return isCachedLocationFresh();
  }, [isCachedLocationFresh]);

  /**
   * Request location from browser
   * - Returns cached location if < 5 minutes old
   * - Deduplicates simultaneous requests
   * - Only triggers permission prompt on user gesture (button click)
   */
  const requestLocation = useCallback(async (): Promise<Location | null> => {
    // Return cached location if fresh
    if (isCachedLocationFresh()) {
      console.log('[LocationContext] Using cached location (< 5 min old)');
      return location;
    }

    // If request already in progress, return the existing promise
    if (requestInProgressRef.current && pendingPromiseRef.current) {
      console.log('[LocationContext] Request already in progress, waiting...');
      return pendingPromiseRef.current;
    }

    // Mark request as in progress
    requestInProgressRef.current = true;
    setIsLoading(true);
    setError(null);

    const promise = new Promise<Location | null>(async (resolve) => {
      // Use Capacitor native API on iOS/Android, browser API on web
      if (isNativeApp()) {
        console.log('[LocationContext] Using Capacitor native geolocation API...');
        try {
          const newLocation = await requestNativeLocation(10000);

          console.log('[LocationContext] Native location acquired:', newLocation);

          // Save to localStorage for 5-minute cache persistence
          try {
            localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(newLocation));
            console.log('[LocationContext] Location saved to localStorage');
          } catch (storageError) {
            console.error('[LocationContext] Error saving location to localStorage:', storageError);
          }

          setLocation(newLocation);
          setPermissionStatus('granted');
          setError(null);
          setIsLoading(false);
          requestInProgressRef.current = false;
          pendingPromiseRef.current = null;
          resolve(newLocation);
        } catch (nativeError) {
          const errorMsg = nativeError instanceof Error ? nativeError.message : 'Failed to get native location';
          console.error('[LocationContext] Native location error:', errorMsg);
          setError(errorMsg);
          setPermissionStatus('denied');
          setIsLoading(false);
          requestInProgressRef.current = false;
          pendingPromiseRef.current = null;
          resolve(null);
        }
        return;
      }

      // Browser/PWA geolocation API
      if (!navigator.geolocation) {
        const errorMsg = 'Geolocation is not supported by your browser';
        setError(errorMsg);
        setPermissionStatus('denied');
        setIsLoading(false);
        requestInProgressRef.current = false;
        pendingPromiseRef.current = null;
        resolve(null);
        return;
      }

      console.log('[LocationContext] Requesting fresh location from browser...');

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          let newLocation: Location = toLocation(position);
          if (!isUsableFix(newLocation)) {
            console.log(`[LocationContext] First fix is coarse (±${Math.round(newLocation.accuracy || 0)} m), waiting for GPS...`);
            newLocation = await refineBrowserFix(newLocation);
          }

          console.log('[LocationContext] Location acquired:', newLocation);

          // Save to localStorage for 5-minute cache persistence
          try {
            localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(newLocation));
            console.log('[LocationContext] Location saved to localStorage');
          } catch (error) {
            console.error('[LocationContext] Error saving location to localStorage:', error);
          }

          setLocation(newLocation);
          setPermissionStatus('granted');
          setError(null);
          setIsLoading(false);
          requestInProgressRef.current = false;
          pendingPromiseRef.current = null;
          resolve(newLocation);
        },
        (error) => {
          let errorMsg = 'Failed to get location';
          let status: 'denied' | 'prompt' = 'denied';

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = 'Location permission denied. Please enable location in your browser settings.';
              status = 'denied';
              // Show iOS-specific modal for PWA users
              if (isIOSDevice() && !isNativeApp()) {
                setShowIOSPermissionModal(true);
              }
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = 'Location information unavailable. Please check your device settings.';
              break;
            case error.TIMEOUT:
              errorMsg = 'Location request timed out. Please try again.';
              status = 'prompt'; // Can retry
              break;
          }

          console.error('[LocationContext] Location error:', errorMsg);
          setError(errorMsg);
          setPermissionStatus(status);
          setIsLoading(false);
          requestInProgressRef.current = false;
          pendingPromiseRef.current = null;
          resolve(null);
        },
        {
          enableHighAccuracy: true,
          timeout: 30000, // 30 seconds
          maximumAge: 0 // Always get fresh location when we explicitly request it
        }
      );
    });

    pendingPromiseRef.current = promise;
    return promise;
  }, [location, isCachedLocationFresh]);

  /**
   * Ensure location is fresh before navigating to a module
   * Call this on module click from dashboards
   * - If location is fresh (< 5 min), returns immediately
   * - Also checks localStorage as fallback if state is out of sync
   * - If stale or missing, requests new location and waits for it
   * - Safe to call on user gesture (button click) - complies with iOS Safari
   */
  const ensureFreshLocation = useCallback(async (): Promise<Location | null> => {
    // First check React state
    if (isCachedLocationFresh()) {
      const age = getLocationAge();
      console.log(`[LocationContext] Location fresh in state (${Math.round((age || 0) / 1000 / 60)} min old)`);
      return location;
    }

    // Fallback: Check localStorage directly (iOS Safari can have state/localStorage sync issues)
    try {
      const cached = localStorage.getItem(LOCATION_STORAGE_KEY);
      if (cached) {
        const parsed: Location = JSON.parse(cached);
        const age = Date.now() - parsed.timestamp;
        if (age < LOCATION_CACHE_DURATION && isUsableFix(parsed)) {
          console.log(`[LocationContext] Location fresh in localStorage (${Math.round(age / 1000 / 60)} min old), syncing to state`);
          setLocation(parsed);
          return parsed;
        }
      }
    } catch (e) {
      console.error('[LocationContext] Error reading localStorage:', e);
    }

    console.log('[LocationContext] No fresh location in state or localStorage, requesting new...');

    // Request fresh location from browser
    return requestLocation();
  }, [location, getLocationAge, isCachedLocationFresh, requestLocation]);

  /**
   * Force a fresh location request, bypassing the cache
   * Use this when user explicitly wants to refresh their location
   * - Sets loading state FIRST to prevent UI flash showing "location required"
   * - Uses very recent cached location (< 30s) for speed during clock actions (unless bypassCache=true)
   * - KEEPS old location if request fails (critical for iOS reliability)
   * - Safe to call on user gesture (button click) - complies with iOS Safari
   * @param bypassCache - If true, always request fresh GPS even if location is recent (use for explicit refresh buttons)
   */
  const forceRefreshLocation = useCallback(async (bypassCache: boolean = false): Promise<Location | null> => {
    console.log('[LocationContext] Force refreshing location...', bypassCache ? '(bypassing cache)' : '');

    // OPTIMIZATION: If location is very recent (< 30 seconds), use it immediately
    // This speeds up clock-in/out significantly - user can't move outside geofence in 30s
    // Skip this optimization if bypassCache is true (user explicitly clicked refresh)
    if (!bypassCache) {
      const RECENT_THRESHOLD_MS = 30 * 1000; // 30 seconds
      const currentAge = getLocationAge();
      if (location && currentAge !== null && currentAge < RECENT_THRESHOLD_MS) {
        console.log(`[LocationContext] Using recent location (${Math.round(currentAge / 1000)}s old)`);
        return location;
      }
    }

    // If request already in progress, return the existing promise
    if (requestInProgressRef.current && pendingPromiseRef.current) {
      console.log('[LocationContext] Force refresh: request already in progress, waiting...');
      return pendingPromiseRef.current;
    }

    // Set loading state but DON'T clear existing location yet
    setIsLoading(true);
    setError(null);
    requestInProgressRef.current = true;

    // On failure we fall back to the existing location ONLY while it's inside the
    // cache window — an older fix can place a worker inside the wrong job's geofence.
    const usableFallback = (): Location | null => {
      if (location && Date.now() - location.timestamp < LOCATION_CACHE_DURATION && isUsableFix(location)) {
        return location;
      }
      console.log('[LocationContext] Existing location too old to reuse after failed refresh');
      return null;
    };

    const promise = new Promise<Location | null>(async (resolve) => {
      // Use BackgroundGeolocation on iOS/Android, browser API on web
      if (isNativeApp()) {
        console.log('[LocationContext] Force requesting fresh GPS via BackgroundGeolocation...');
        try {
          const newLocation = await requestNativeLocation(8000); // 8 second timeout for faster response

          console.log('[LocationContext] Fresh native location acquired:', newLocation);

          // Only now update cache and state with new location
          try {
            localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(newLocation));
          } catch (e) {
            console.error('[LocationContext] Error saving to localStorage:', e);
          }

          setLocation(newLocation);
          setPermissionStatus('granted');
          setError(null);
          setIsLoading(false);
          requestInProgressRef.current = false;
          pendingPromiseRef.current = null;
          resolve(newLocation);
        } catch (nativeError) {
          const errorMsg = nativeError instanceof Error ? nativeError.message : 'Failed to get native location';
          console.error('[LocationContext] Force refresh failed (native):', errorMsg);
          setError(errorMsg);
          setIsLoading(false);
          requestInProgressRef.current = false;
          pendingPromiseRef.current = null;

          // CRITICAL: Keep existing location on failure - don't lose it!
          console.log('[LocationContext] Keeping existing location after failed native refresh');
          resolve(usableFallback());
        }
        return;
      }

      // Browser/PWA geolocation API
      if (!navigator.geolocation) {
        setError('Geolocation is not supported by your browser');
        setIsLoading(false);
        requestInProgressRef.current = false;
        pendingPromiseRef.current = null;
        // Keep existing location on failure
        resolve(usableFallback());
        return;
      }

      console.log('[LocationContext] Force requesting fresh GPS...');

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          let newLocation: Location = toLocation(position);
          if (!isUsableFix(newLocation)) {
            console.log(`[LocationContext] Forced fix is coarse (±${Math.round(newLocation.accuracy || 0)} m), waiting for GPS...`);
            newLocation = await refineBrowserFix(newLocation);
          }

          console.log('[LocationContext] Fresh location acquired:', newLocation);

          // Only now update cache and state with new location
          try {
            localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(newLocation));
          } catch (e) {
            console.error('[LocationContext] Error saving to localStorage:', e);
          }

          setLocation(newLocation);
          setPermissionStatus('granted');
          setError(null);
          setIsLoading(false);
          requestInProgressRef.current = false;
          pendingPromiseRef.current = null;
          resolve(newLocation);
        },
        (geoError) => {
          console.error('[LocationContext] Force refresh failed:', geoError.message);

          let errorMsg = 'Failed to get location';
          if (geoError.code === geoError.PERMISSION_DENIED) {
            errorMsg = 'Location permission denied';
            setPermissionStatus('denied');
            // Show iOS-specific modal for PWA users
            if (isIOSDevice() && !isNativeApp()) {
              setShowIOSPermissionModal(true);
            }
          } else if (geoError.code === geoError.TIMEOUT) {
            errorMsg = 'Location request timed out';
            setPermissionStatus('prompt');
          }

          setError(errorMsg);
          setIsLoading(false);
          requestInProgressRef.current = false;
          pendingPromiseRef.current = null;

          // CRITICAL: Keep existing location on failure - don't lose it!
          console.log('[LocationContext] Keeping existing location after failed refresh');
          resolve(usableFallback());
        },
        {
          enableHighAccuracy: true,
          timeout: 30000, // 30 seconds
          maximumAge: 0
        }
      );
    });

    pendingPromiseRef.current = promise;
    return promise;
  }, [location, getLocationAge]);

  /**
   * Clear cached location (useful for testing or manual refresh)
   */
  const clearLocation = useCallback(() => {
    setLocation(null);
    setError(null);
    localStorage.removeItem(LOCATION_STORAGE_KEY);
    console.log('[LocationContext] Location cache cleared (including localStorage)');
  }, []);

  /**
   * Auto-refresh location for native apps:
   * - On cold start (first app open)
   * - On app resume (from background)
   * Only triggers if location is expired AND permission already granted (no prompt)
   */
  useEffect(() => {
    if (!isNativeApp()) {
      console.log('[LocationContext] Skipping auto-location (not native app)');
      return;
    }

    console.log('[LocationContext] Setting up native app auto-location detection');

    // Initialize background geolocation service on app startup
    // This restores tracking state if user was clocked in before app was force closed
    backgroundGeolocationService.initialize().then(initialized => {
      if (initialized) {
        console.log('[LocationContext] Background geolocation service initialized (tracking restored if needed)');
      }
    }).catch(err => {
      console.error('[LocationContext] Error initializing background geolocation service:', err);
    });

    const checkAndRefreshLocation = async (source: 'cold_start' | 'resume' | 'active') => {
      try {
        // Check if location is expired
        const cached = localStorage.getItem(LOCATION_STORAGE_KEY);
        let isExpired = true;

        if (cached) {
          const parsed: Location = JSON.parse(cached);
          const age = Date.now() - parsed.timestamp;
          isExpired = age >= LOCATION_CACHE_DURATION || !isUsableFix(parsed);

          if (!isExpired) {
            console.log(`[LocationContext] ${source} - location still fresh, skipping refresh`);
            return;
          }
        }

        console.log(`[LocationContext] ${source} - location expired, checking permission...`);

        // Check if permission is already granted (don't trigger prompt)
        // Use BackgroundGeolocation to check - same plugin as background tracking
        const providerState = await BackgroundGeolocation.getProviderState();

        // AUTHORIZATION_STATUS_ALWAYS = 3, AUTHORIZATION_STATUS_WHEN_IN_USE = 2
        if (providerState.status < 2) {
          console.log(`[LocationContext] ${source} - permission not granted, skipping silent refresh`);
          return;
        }

        console.log(`[LocationContext] ${source} - silently refreshing expired location...`);

        // Silently get fresh location using BackgroundGeolocation (permission already granted, no prompt)
        const position = await BackgroundGeolocation.getCurrentPosition({
          samples: 1,
          timeout: 10, // 10 seconds
          desiredAccuracy: BackgroundGeolocation.DESIRED_ACCURACY_HIGH,
          persist: false
        });

        const newLocation: Location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy ?? null,
          timestamp: Date.now()
        };

        // Update cache and state
        localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(newLocation));
        setLocation(newLocation);
        setPermissionStatus('granted');

        console.log(`[LocationContext] ${source} - location refreshed silently:`, newLocation);
      } catch (err) {
        // Fail silently - user can refresh manually when needed
        console.log(`[LocationContext] ${source} - silent refresh failed (will retry on user action):`, err);
      }
    };

    // Auto-detect on cold start (first app open)
    console.log('[LocationContext] Checking location on cold start...');
    checkAndRefreshLocation('cold_start');

    // Listen for app becoming active (from background)
    const stateListener = App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        console.log('[LocationContext] App became active');
        checkAndRefreshLocation('active');
      }
    });

    // Also listen for resume event (more reliable on some devices)
    const resumeListener = App.addListener('resume', () => {
      console.log('[LocationContext] App resumed from background');
      checkAndRefreshLocation('resume');
    });

    // Cleanup listeners on unmount
    return () => {
      stateListener.then(handle => handle.remove());
      resumeListener.then(handle => handle.remove());
      console.log('[LocationContext] Cleaned up app lifecycle listeners');
    };
  }, []); // Empty deps - only set up once on mount

  return (
    <LocationContext.Provider
      value={{
        location,
        permissionStatus,
        isLoading,
        error,
        requestLocation,
        ensureFreshLocation,
        forceRefreshLocation,
        isLocationFresh,
        clearLocation,
        getLocationAge
      }}
    >
      {children}

      {/* iOS PWA Location Permission Denied Modal */}
      {showIOSPermissionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#1e293b',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '380px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            border: '1px solid #334155'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <MapPin size={28} color="white" />
                </div>
                <div>
                  <h2 style={{ color: 'white', fontSize: '18px', fontWeight: '700', margin: 0 }}>
                    Location Disabled
                  </h2>
                  <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
                    Permission Required
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowIOSPermissionModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '8px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Warning */}
            <div style={{
              backgroundColor: '#fef3c7',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start'
            }}>
              <AlertTriangle size={24} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
              <p style={{ color: '#92400e', fontSize: '14px', margin: 0, lineHeight: '1.5' }}>
                Location access was denied. On iOS, you must close and reopen the app to see the permission prompt again.
              </p>
            </div>

            {/* Steps */}
            <div style={{ marginBottom: '24px' }}>
              <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '16px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Follow these steps:
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Step 1 */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>1</div>
                  <div>
                    <p style={{ color: 'white', fontSize: '15px', fontWeight: '600', margin: '0 0 4px 0' }}>
                      Swipe Up from Bottom
                    </p>
                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
                      Open the app switcher by swiping up from the bottom of the screen and pausing
                    </p>
                  </div>
                </div>

                {/* Step 2 */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>2</div>
                  <div>
                    <p style={{ color: 'white', fontSize: '15px', fontWeight: '600', margin: '0 0 4px 0' }}>
                      Swipe Up on This App
                    </p>
                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
                      Find this app in the switcher and swipe it up to force close
                    </p>
                  </div>
                </div>

                {/* Step 3 */}
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: '700',
                    flexShrink: 0
                  }}>3</div>
                  <div>
                    <p style={{ color: 'white', fontSize: '15px', fontWeight: '600', margin: '0 0 4px 0' }}>
                      Reopen & Allow Location
                    </p>
                    <p style={{ color: '#94a3b8', fontSize: '13px', margin: 0 }}>
                      Open the app again and tap <strong style={{ color: '#22c55e' }}>"Allow"</strong> when prompted for location
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Close Button */}
            <button
              onClick={() => setShowIOSPermissionModal(false)}
              style={{
                width: '100%',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '16px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Got It
            </button>
          </div>
        </div>
      )}
    </LocationContext.Provider>
  );
};

/**
 * Hook to access location context
 * Must be used within LocationProvider
 */
export const useLocation = (): LocationContextType => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};
